import * as t from './types'
import * as api from './api'
import * as effects from 'redux-saga/effects'
import { pipe } from 'fp-ts/lib/function'
import { taskEither, task, either, record, array, option, string } from 'fp-ts'
import {
  extractDataFromWorkBook,
  defaultMapping,
  Customer,
  NewSupplierInvoice,
  NewOrganizationInvoice,
} from '../../types'
import * as xlsx from 'xlsx'
import { takeLatest, waitForOrganization } from '../../saga'
import { State } from '../../store'
import { CancelToken } from 'axios'
import { getPayerByEmailOrPhonenumber } from '../Payers/api'
import { eqCustomer } from '../Payers'
import {
  Organization,
  result,
  NotRequested,
  Loading,
  foldEitherToAction,
} from '@library/react-toolkit'

export const initialState: t.State = { state: 'Closed' }

export const reducer = (s: t.State = initialState, a: t.Actions): t.State => {
  switch (a.type) {
    case 'UPLOAD/start_wizard':
      return { state: 'Open' }
    case 'UPLOAD/select_file':
      return { state: 'Loading' }
    case 'UPLOAD/parse_file_result':
      return pipe(
        a.payload,
        either.fold(
          (e): t.State => ({ state: 'Error', error: e }),
          (data): t.State => ({
            data,
            state: 'Select',
            selectedSheets: record.keys(data),
          })
        )
      )
    case 'UPLOAD/cancel':
      return initialState
    case 'UPLOAD/select_sheet':
      return s.state === 'Select'
        ? {
            ...s,
            selectedSheets: array.elem(string.Eq)(a.payload, s.selectedSheets)
              ? pipe(
                  s.selectedSheets,
                  array.filter(v => v !== a.payload)
                )
              : [a.payload, ...s.selectedSheets],
          }
        : s
    case 'UPLOAD/set_assignment':
      return s.state === 'Assign'
        ? {
            ...s,
            assignments: {
              ...s.assignments,
              [a.payload[0]]: {
                ...s.assignments[a.payload[0]],
                [a.payload[1]]: a.payload[2],
              },
            },
          }
        : s
    case 'UPLOAD/set_payer_memo':
      return s.state === 'Assign' ? { ...s, memo: a.payload } : s
    case 'UPLOAD/step_back':
      return s.state === 'Select'
        ? { state: 'Open' }
        : s.state === 'Confirm'
        ? { ...s, state: 'Assign' }
        : s.state === 'Assign'
        ? { state: 'Select', data: s.data, selectedSheets: s.selectedSheets }
        : s
    case 'UPLOAD/step_forward':
      return s.state === 'Select' && s.selectedSheets.length > 0
        ? {
            ...s,
            state: 'Assign',
            assignments: pipe(
              s.data,
              record.filterMapWithIndex((k, v) =>
                array.elem(string.Eq)(k, s.selectedSheets)
                  ? option.some(defaultMapping(a.payload, v))
                  : option.none
              )
            ),
            memo: [],
          }
        : s.state === 'Assign'
        ? {
            ...s,
            state: 'Confirm',
            finalData: t.finalDataFromConfirmState(s.data, s.assignments),
            uploadResult: NotRequested,
          }
        : s
    case 'UPLOAD/confirm_upload':
      return s.state === 'Confirm' ? { ...s, uploadResult: Loading } : s
    case 'UPLOAD/send_result':
      return result.isOk(a.payload)
        ? initialState
        : s.state === 'Confirm'
        ? { ...s, uploadResult: a.payload }
        : s
    default:
      return s
  }
}

function* selectFileSaga(a: t.SelectFileAction) {
  // @ts-ignore
  yield yield pipe(
    taskEither.tryCatch(
      () => a.payload.arrayBuffer(),
      e => (e instanceof Error ? e : new Error(`unknown error: ${e}`))
    ),
    taskEither.map(f => xlsx.read(f, { type: 'buffer' })),
    task.map(either.chain(extractDataFromWorkBook)),
    task.map(t.parseFileResult),
    task.map(actions => effects.put(actions)),
    e => effects.call(e)
  )
}

const findInMemo = (
  memo: Customer[],
  v: option.Option<string>,
  k: 'email' | 'phoneNumber'
) =>
  pipe(
    v,
    option.chain(x =>
      pipe(
        memo,
        array.findFirst(p => p[k] === x)
      )
    )
  )

type PartialSearch = {
  email: option.Option<string>
  phone: option.Option<string>
}

const findMemo = (memo: Customer[], ps: PartialSearch) =>
  pipe(
    findInMemo(memo, ps.email, 'email'),
    option.alt(() => findInMemo(memo, ps.phone, 'phoneNumber'))
  )

const altTO =
  <T>(ta: task.Task<option.Option<T>>) =>
  (tb: task.Task<option.Option<T>>): task.Task<option.Option<T>> =>
    pipe(
      tb,
      task.chain(y =>
        pipe(
          y,
          option.fold(
            () => ta,
            () => task.of(y)
          )
        )
      )
    )

const findRemote = (organization: string, ps: PartialSearch) => {
  const s = {
    email: option.toUndefined(ps.email),
    phone: option.toUndefined(ps.phone),
  }

  if (!s.email && !s.phone) {
    return task.of(option.none)
  }

  return pipe(
    getPayerByEmailOrPhonenumber(organization, s.email, s.phone),
    task.map(option.fromEither)
  )
}

const findPayer = (organization: string, memo: Customer[], ps: PartialSearch) =>
  pipe(task.of(findMemo(memo, ps)), altTO(findRemote(organization, ps)))

function* assignColumnSaga(_: t.SetAssignmentAction) {
  const s: t.State = yield effects.select(({ upload }: State) => upload)
  if (s.state !== 'Assign') {
    return
  }

  const bills = t.finalDataFromConfirmState(s.data, s.assignments)
  const organization: Organization = yield waitForOrganization()

  // @ts-ignore
  yield yield pipe(
    bills,
    array.map(c =>
      findPayer(organization.name, s.memo, {
        email: c.payerEmail,
        phone: c.payerPhonenumber,
      })
    ),
    array.Traversable.sequence(task.Monad),
    task.map(array.compact),
    task.map(array.uniq(eqCustomer)),
    task.map(ps => effects.put(t.setPayerMemo(ps))),
    p => effects.call(p)
  )
}

type FromStore = ReturnType<typeof t.getUploadData> extends (infer T)[]
  ? T
  : never

const asBaseInvoice = (
  v: FromStore,
  organization: { asset: string; name: string }
) => ({
  amount: v.amount,
  reference: v.reference,
  createdAt: v.createdAt,
  dueAt: v.dueAt,
  unitID: organization.asset,
  items: [],
  notifyPhonenumber: undefined,
  urlOk: undefined,
  urlFailed: undefined,
  paidBy: undefined,
  discount:
    !!v.discountName && v.discountRate !== undefined
      ? {
          name: v.discountName,
          rate: {
            numerator: v.discountRate,
            denominator: 100,
          },
          description: '',
        }
      : undefined,
  tax:
    !!v.taxName && v.taxRate !== undefined
      ? {
          name: v.taxName,
          rate: {
            numerator: v.taxRate,
            denominator: 100,
          },
          description: '',
        }
      : undefined,
})

const asOrganizationInvoice = (
  s: FromStore,
  org: { asset: string; name: string }
): NewOrganizationInvoice => ({
  ...asBaseInvoice(s, org),
  customer: {
    id: -1,
    name: s.payerName,
    email: s.payerEmail,
    phoneNumber: s.payerPhonenumber,
    address: s.payerAddress,
    taxNumber: s.payerTaxNumber,
    nymId: undefined,
  },
})

const asSupplierInvoice = (
  s: FromStore,
  org: { asset: string; name: string }
): NewSupplierInvoice => ({
  ...asBaseInvoice(s, org),
  organization: s.organization,
  email: s.email,
})

function* uploadFileSaga(a: t.ConfirmUploadAction, cancelToken?: CancelToken) {
  const s: t.State = yield effects.select(({ upload }: State) => upload)
  if (s.state !== 'Confirm') {
    return
  }

  const dataToUpload = t.getUploadData(s)
  const organization: Organization = yield waitForOrganization()

  // @ts-ignore
  yield yield pipe(
    dataToUpload,
    bs =>
      a.payload === 'organization'
        ? api.addInvoices(
            bs.map(b => asOrganizationInvoice(b, organization)),
            organization.name,
            cancelToken
          )
        : api.addSupplierInvoices(
            bs.map(b => asSupplierInvoice(b, organization)),
            organization.name,
            cancelToken
          ),
    task.map(foldEitherToAction(t.sendResult)),
    task.map(e => effects.put(e)),
    e => effects.call(e)
  )
}

export function* saga() {
  yield effects.all([
    takeLatest('UPLOAD/select_file', selectFileSaga),
    takeLatest('UPLOAD/confirm_upload', uploadFileSaga),
    takeLatest('UPLOAD/set_assignment', assignColumnSaga),
  ])
}
